import React from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid2 as Grid,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Divider,
  MenuItem,
  Paper,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { 
  CreateBossBabyToolDto, 
  UpdateBossBabyToolDto, 
  BossBabyToolDto 
} from '@/dto/BossBabyDto';
import { useBossBabyUtils } from '@/hooks/useBossBaby';

interface ToolFormProps {
  initialData?: BossBabyToolDto;
  onSubmit: (data: CreateBossBabyToolDto | UpdateBossBabyToolDto) => void;
  onCancel: () => void;
  isEdit?: boolean;
}

type FormData = {
  toolName: string;
  description: string;
  category: string;
  schema: string;
  timeoutSeconds: number;
  priorityLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  isActive: boolean;
};

export const ToolForm: React.FC<ToolFormProps> = ({ 
  initialData, 
  onSubmit, 
  onCancel,
  isEdit = false 
}) => {
  const { validateJsonSchema } = useBossBabyUtils();
  
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<FormData>({
    defaultValues: {
      toolName: initialData?.toolName || '',
      description: initialData?.description || '',
      category: initialData?.category || 'boss_baby',
      schema: initialData?.schema ? JSON.stringify(initialData.schema, null, 2) : `{
  "type": "object",
  "properties": {
    "action": {
      "type": "string",
      "description": "Action to perform"
    }
  },
  "required": ["action"]
}`,
      timeoutSeconds: initialData?.timeoutSeconds || 30,
      priorityLevel: initialData?.priorityLevel || 'MEDIUM',
      isActive: initialData?.isActive ?? true,
    }
  });

  const defaultProps = {
    size: "small" as const,
    fullWidth: true,
  };

  const handleFormSubmit = (data: FormData) => {
    try {
      const parsedSchema = JSON.parse(data.schema);
      
      const submitData = {
        ...data,
        schema: parsedSchema,
        description: data.description.trim() || undefined,
      };

      if (isEdit) {
        const updateData: UpdateBossBabyToolDto = {
          description: submitData.description,
          schema: submitData.schema,
          timeoutSeconds: submitData.timeoutSeconds,
          priorityLevel: submitData.priorityLevel,
          isActive: submitData.isActive,
        };
        onSubmit(updateData);
      } else {
        const createData: CreateBossBabyToolDto = {
          toolName: submitData.toolName,
          description: submitData.description,
          category: submitData.category,
          schema: submitData.schema,
          timeoutSeconds: submitData.timeoutSeconds,
          priorityLevel: submitData.priorityLevel,
        };
        onSubmit(createData);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const validateSchema = (value: string) => {
    if (!value.trim()) return 'Schema is required';
    if (!validateJsonSchema(value)) return 'Invalid JSON format';
    return true;
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          {isEdit ? 'Edit Boss Baby Tool' : 'Create Boss Baby Tool'}
        </Typography>

        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Basic Information
            </Typography>
          </Grid>

          {/* Tool Name */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <FormLabel required>Tool Name</FormLabel>
              <Controller
                name="toolName"
                control={control}
                rules={{ 
                  required: 'Tool name is required',
                  pattern: {
                    value: /^[a-zA-Z0-9_]+$/,
                    message: 'Only letters, numbers, and underscores allowed'
                  },
                  maxLength: {
                    value: 255,
                    message: 'Maximum 255 characters'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    {...defaultProps}
                    disabled={isEdit}
                    error={!!errors.toolName}
                    helperText={errors.toolName?.message}
                    placeholder="e.g., ScheduleManagementTool"
                  />
                )}
              />
            </FormControl>
          </Grid>

          {/* Category */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <FormLabel required>Category</FormLabel>
              <Controller
                name="category"
                control={control}
                rules={{ required: 'Category is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    {...defaultProps}
                    select
                    error={!!errors.category}
                    helperText={errors.category?.message}
                  >
                    <MenuItem value="boss_baby">Boss Baby</MenuItem>
                    <MenuItem value="utility">Utility</MenuItem>
                    <MenuItem value="integration">Integration</MenuItem>
                  </TextField>
                )}
              />
            </FormControl>
          </Grid>

          {/* Description */}
          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth>
              <FormLabel>Description</FormLabel>
              <Controller
                name="description"
                control={control}
                rules={{
                  maxLength: {
                    value: 1000,
                    message: 'Maximum 1000 characters'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    {...defaultProps}
                    multiline
                    rows={3}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    placeholder="Brief description of what this tool does"
                  />
                )}
              />
            </FormControl>
          </Grid>

          {/* Configuration */}
          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Configuration
            </Typography>
          </Grid>

          {/* Priority Level */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <FormLabel required>Priority Level</FormLabel>
              <Controller
                name="priorityLevel"
                control={control}
                rules={{ required: 'Priority level is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    {...defaultProps}
                    select
                    error={!!errors.priorityLevel}
                    helperText={errors.priorityLevel?.message}
                  >
                    <MenuItem value="LOW">Low</MenuItem>
                    <MenuItem value="MEDIUM">Medium</MenuItem>
                    <MenuItem value="HIGH">High</MenuItem>
                  </TextField>
                )}
              />
            </FormControl>
          </Grid>

          {/* Timeout */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <FormLabel required>Timeout (seconds)</FormLabel>
              <Controller
                name="timeoutSeconds"
                control={control}
                rules={{ 
                  required: 'Timeout is required',
                  min: { value: 1, message: 'Minimum 1 second' },
                  max: { value: 300, message: 'Maximum 300 seconds' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    {...defaultProps}
                    type="number"
                    error={!!errors.timeoutSeconds}
                    helperText={errors.timeoutSeconds?.message}
                  />
                )}
              />
            </FormControl>
          </Grid>

          {/* Schema */}
          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth>
              <FormLabel required>Schema (JSON)</FormLabel>
              <Controller
                name="schema"
                control={control}
                rules={{ 
                  required: 'Schema is required',
                  validate: validateSchema
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    {...defaultProps}
                    multiline
                    rows={8}
                    error={!!errors.schema}
                    helperText={errors.schema?.message || 'Define the tool parameters in JSON format'}
                    sx={{ 
                      '& .MuiInputBase-input': { 
                        fontFamily: 'monospace',
                        fontSize: '0.875rem'
                      }
                    }}
                  />
                )}
              />
            </FormControl>
          </Grid>

          {/* Status */}
          {isEdit && (
            <>
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Status
                </Typography>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormControl>
                  <Controller
                    name="isActive"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={field.onChange}
                          />
                        }
                        label="Active"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </>
          )}

          {/* Actions */}
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
              <Button 
                variant="outlined" 
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : (isEdit ? 'Update Tool' : 'Create Tool')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};